import React, { useState, useRef, useEffect, useCallback } from 'react';
import { VideoTrimmer } from './VideoTrimmer';

const MAX_BUFFER_DURATION = 120; // Keep up to 2 minutes
// Segmented recording (2026-07-12 rework): instead of one endless MediaRecorder
// sliced into 1s chunks (whose VP9 frames reference pruned data — windows lost
// up to ~3.4s of garbage at the start), we rotate the recorder every
// SEGMENT_SECONDS. Every retained segment is a complete standalone webm that
// begins with its own init segment + keyframe, so any window of segments is
// fully decodable. The server concats them losslessly and trims exactly.
const SEGMENT_SECONDS = 10;
const MAX_SEGMENTS = Math.ceil(MAX_BUFFER_DURATION / SEGMENT_SECONDS); // 12

// Replay durations in seconds
const REPLAY_OPTIONS = [
  { label: '30 sec', seconds: 30 },
  { label: '1 min', seconds: 60 },
  { label: 'Full', seconds: -1 },  // -1 means full video
];

// Preferred codecs
const PREFERRED_CODECS = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

function getBestCodec() {
  for (const codec of PREFERRED_CODECS) {
    if (MediaRecorder.isTypeSupported(codec)) {
      return codec;
    }
  }
  return '';
}

export function InstantReplay({ videoTracks, audioTracks, matrixRoomId, client }) {
  const [isRecording, setIsRecording] = useState(false);
  const [bufferDuration, setBufferDuration] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isReplaying, setIsReplaying] = useState(false);
  const [replayUrl, setReplayUrl] = useState(null);
  // Server-side replay session id — trim and Matrix upload reference this id
  // instead of re-uploading the video bytes (2026-07-12 rework).
  const [replayId, setReplayId] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [debugInfo, setDebugInfo] = useState('');
  const [showTrimmer, setShowTrimmer] = useState(false);

  // Ring of complete segments: [{ blob, startTs, endTs }] — newest last.
  const segmentsRef = useRef([]);
  const activeRecorderRef = useRef(null);
  const activeChunksRef = useRef([]);
  const activeStartTsRef = useRef(null);
  const keepRecordingRef = useRef(false);
  const rotateTimerRef = useRef(null);
  // Resolvers waiting for the in-flight segment to finalize (snapshot path).
  const finalizeWaitersRef = useRef([]);

  // Authorization header for the (now authenticated) replay API.
  const authHeaders = useCallback(() => {
    const token = client?.getAccessToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [client]);
  const replayVideoRef = useRef(null);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const mixedStreamRef = useRef(null);
  const videoElementsRef = useRef([]);
  const animationFrameRef = useRef(null);
  const bufferUpdateIntervalRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording();
      if (replayUrl && replayUrl.startsWith('blob:')) {
        URL.revokeObjectURL(replayUrl);
      }
    };
  }, []);

  // Start/stop recording based on tracks
  useEffect(() => {
    if (videoTracks.length === 0 && audioTracks.length === 0) {
      stopRecording();
      return;
    }

    // Small delay to let tracks initialize
    const timer = setTimeout(() => {
      startRecording();
    }, 500);

    return () => {
      clearTimeout(timer);
      stopRecording();
    };
  }, [videoTracks.length, audioTracks.length]);

  const createMixedStream = useCallback(async () => {
    try {
      console.log('[Replay] Creating mixed stream with', videoTracks.length, 'video and', audioTracks.length, 'audio tracks');

      // Create canvas for video mixing
      if (!canvasRef.current) {
        canvasRef.current = document.createElement('canvas');
        canvasRef.current.width = 1920;
        canvasRef.current.height = 1080;
      }
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d', {
        alpha: false,
        willReadFrequently: false,
      });

      // Clean up old video elements
      videoElementsRef.current.forEach(v => {
        v.pause();
        v.srcObject = null;
      });
      videoElementsRef.current = [];

      // Create video elements for each track
      const videoPromises = [];
      for (const [id, { track }] of videoTracks) {
        const video = document.createElement('video');
        video.autoplay = true;
        video.muted = true;
        video.playsInline = true;
        video.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;';
        document.body.appendChild(video); // Must be in DOM for some browsers

        // Get the MediaStreamTrack from LiveKit track
        const mediaStreamTrack = track.mediaStreamTrack;
        if (mediaStreamTrack) {
          video.srcObject = new MediaStream([mediaStreamTrack]);

          // Create a promise that resolves when video is ready
          const readyPromise = new Promise((resolve) => {
            if (video.readyState >= 2 && video.videoWidth > 0) {
              resolve();
            } else {
              video.onloadeddata = () => resolve();
              video.oncanplay = () => resolve();
              // Timeout after 3 seconds
              setTimeout(resolve, 3000);
            }
          });
          videoPromises.push(readyPromise);

          // Force play
          video.play().catch(e => console.warn('[Replay] Video play failed:', e));
          videoElementsRef.current.push(video);
          console.log('[Replay] Added video track:', id, 'readyState:', video.readyState, 'trackEnabled:', mediaStreamTrack.enabled, 'trackReadyState:', mediaStreamTrack.readyState);
        }
      }

      // Wait for at least one video to be ready (or timeout)
      if (videoPromises.length > 0) {
        await Promise.race([
          Promise.any(videoPromises),
          new Promise(resolve => setTimeout(resolve, 3000))
        ]);
        console.log('[Replay] Videos after wait:', videoElementsRef.current.map((v, i) =>
          `Video ${i}: ${v.videoWidth}x${v.videoHeight}, readyState=${v.readyState}`
        ).join(', '));
      }

      // Create audio context for mixing
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch (e) {}
      }
      audioContextRef.current = new AudioContext();
      const audioCtx = audioContextRef.current;
      const destination = audioCtx.createMediaStreamDestination();

      // Add audio tracks
      for (const [id, { track }] of audioTracks) {
        try {
          const mediaStreamTrack = track.mediaStreamTrack;
          if (mediaStreamTrack) {
            const stream = new MediaStream([mediaStreamTrack]);
            const source = audioCtx.createMediaStreamSource(stream);
            source.connect(destination);
            console.log('[Replay] Added audio track:', id);
          }
        } catch (e) {
          console.error('[Replay] Failed to add audio:', e);
        }
      }

      // Render loop with debug
      let frameCount = 0;
      let hasDrawnVideo = false;
      const renderFrame = () => {
        if (!canvasRef.current) return;

        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const videos = videoElementsRef.current.filter(v => v.readyState >= 2 && v.videoWidth > 0);

        // Debug: log every 60 frames (about every 2 seconds)
        frameCount++;
        if (frameCount % 60 === 0) {
          console.log('[Replay] Frame', frameCount, '- Ready videos:', videos.length, '/', videoElementsRef.current.length, '- hasDrawnVideo:', hasDrawnVideo);
          videoElementsRef.current.forEach((v, i) => {
            console.log(`  Video ${i}: readyState=${v.readyState}, dimensions=${v.videoWidth}x${v.videoHeight}, paused=${v.paused}, ended=${v.ended}`);
          });
        }

        if (videos.length === 1) {
          try {
            ctx.drawImage(videos[0], 0, 0, canvas.width, canvas.height);
            hasDrawnVideo = true;
          } catch (e) {
            console.error('[Replay] drawImage error:', e);
          }
        } else if (videos.length > 1) {
          const cols = Math.ceil(Math.sqrt(videos.length));
          const rows = Math.ceil(videos.length / cols);
          const cellW = canvas.width / cols;
          const cellH = canvas.height / rows;

          videos.forEach((video, i) => {
            try {
              const col = i % cols;
              const row = Math.floor(i / cols);
              ctx.drawImage(video, col * cellW, row * cellH, cellW, cellH);
              hasDrawnVideo = true;
            } catch (e) {
              console.error('[Replay] drawImage error for video', i, ':', e);
            }
          });
        } else if (!hasDrawnVideo) {
          // Draw a test pattern if no video is available
          ctx.fillStyle = '#333';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = '#666';
          ctx.font = '48px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('Waiting for video...', canvas.width / 2, canvas.height / 2);
          ctx.font = '24px sans-serif';
          ctx.fillText(`Frame ${frameCount}`, canvas.width / 2, canvas.height / 2 + 40);
        }

        animationFrameRef.current = requestAnimationFrame(renderFrame);
      };

      renderFrame();

      // Create mixed stream
      const canvasStream = canvas.captureStream(30);
      const tracks = [
        ...canvasStream.getVideoTracks(),
        ...destination.stream.getAudioTracks(),
      ];

      mixedStreamRef.current = new MediaStream(tracks);
      console.log('[Replay] Mixed stream created with', tracks.length, 'tracks');
      return mixedStreamRef.current;
    } catch (e) {
      console.error('[Replay] Failed to create mixed stream:', e);
      setDebugInfo('Error: ' + e.message);
      return null;
    }
  }, [videoTracks, audioTracks]);

  // Start ONE segment recorder on the mixed stream. When it stops (rotation,
  // snapshot finalize, or shutdown) the collected chunks become a complete,
  // independently-decodable webm segment; recording chains straight into the
  // next segment while keepRecordingRef is set.
  const startSegmentRecorder = useCallback((stream) => {
    const mimeType = getBestCodec();
    const options = { videoBitsPerSecond: 4000000 };
    if (mimeType) options.mimeType = mimeType;

    const recorder = new MediaRecorder(stream, options);
    activeChunksRef.current = [];
    activeStartTsRef.current = Date.now();

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) activeChunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const chunks = activeChunksRef.current;
      activeChunksRef.current = [];
      if (chunks.length > 0) {
        const mime = getBestCodec() || 'video/webm';
        segmentsRef.current.push({
          blob: new Blob(chunks, { type: mime }),
          startTs: activeStartTsRef.current,
          endTs: Date.now(),
        });
        // Bound memory: keep MAX_SEGMENTS complete segments.
        while (segmentsRef.current.length > MAX_SEGMENTS) {
          segmentsRef.current.shift();
        }
      }
      // Wake anyone waiting for this segment to finalize (snapshot path).
      const waiters = finalizeWaitersRef.current;
      finalizeWaitersRef.current = [];
      waiters.forEach((resolve) => resolve());

      if (keepRecordingRef.current && mixedStreamRef.current) {
        startSegmentRecorder(mixedStreamRef.current);
      }
    };

    recorder.onerror = (e) => {
      console.error('[Replay] Recorder error:', e);
      setDebugInfo('Recorder error');
    };

    recorder.start();
    activeRecorderRef.current = recorder;
  }, []);

  // Rotate: finalize the current segment (onstop chains the next recorder).
  const rotateSegment = useCallback(() => {
    const rec = activeRecorderRef.current;
    if (rec && rec.state === 'recording') rec.stop();
  }, []);

  // Force-finalize the in-flight segment so a snapshot includes the freshest
  // video, and resolve once it has landed in segmentsRef. Recording continues
  // seamlessly (onstop restarts a new segment recorder).
  const finalizeActiveSegment = useCallback(() => {
    const rec = activeRecorderRef.current;
    if (!rec || rec.state !== 'recording') return Promise.resolve();
    return new Promise((resolve) => {
      finalizeWaitersRef.current.push(resolve);
      rec.stop();
      // Safety: never hang the UI if onstop misbehaves.
      setTimeout(resolve, 2000);
    });
  }, []);

  const startRecording = useCallback(async () => {
    if (activeRecorderRef.current) {
      console.log('[Replay] Already recording');
      return;
    }

    const mixedStream = await createMixedStream();
    if (!mixedStream) {
      setDebugInfo('Failed to create stream');
      return;
    }

    try {
      segmentsRef.current = [];
      keepRecordingRef.current = true;
      startSegmentRecorder(mixedStream);
      rotateTimerRef.current = setInterval(rotateSegment, SEGMENT_SECONDS * 1000);
      setIsRecording(true);

      // Update buffer duration display (complete segments + in-flight one)
      bufferUpdateIntervalRef.current = setInterval(() => {
        const seg = segmentsRef.current.reduce(
          (sum, s) => sum + (s.endTs - s.startTs) / 1000, 0);
        const active = activeStartTsRef.current
          ? (Date.now() - activeStartTsRef.current) / 1000 : 0;
        setBufferDuration(Math.min(seg + active, MAX_BUFFER_DURATION));
      }, 1000);

      const mimeType = getBestCodec();
      console.log('[Replay] Started segmented recording with', mimeType || 'default codec');
      setDebugInfo('Recording: ' + (mimeType || 'default'));
    } catch (e) {
      console.error('[Replay] Failed to start recording:', e);
      setDebugInfo('Start error: ' + e.message);
    }
  }, [createMixedStream, startSegmentRecorder, rotateSegment]);

  const stopRecording = useCallback(() => {
    keepRecordingRef.current = false;

    if (rotateTimerRef.current) {
      clearInterval(rotateTimerRef.current);
      rotateTimerRef.current = null;
    }

    if (bufferUpdateIntervalRef.current) {
      clearInterval(bufferUpdateIntervalRef.current);
      bufferUpdateIntervalRef.current = null;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (activeRecorderRef.current) {
      if (activeRecorderRef.current.state === 'recording') {
        activeRecorderRef.current.stop();
      }
      activeRecorderRef.current = null;
    }

    segmentsRef.current = [];
    activeChunksRef.current = [];
    activeStartTsRef.current = null;

    videoElementsRef.current.forEach(v => {
      v.pause();
      v.srcObject = null;
      if (v.parentNode) {
        v.parentNode.removeChild(v);
      }
    });
    videoElementsRef.current = [];

    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (e) {}
      audioContextRef.current = null;
    }

    setIsRecording(false);
    setBufferDuration(0);
  }, []);

  const createReplay = useCallback(async (seconds) => {
    if (!keepRecordingRef.current) {
      console.log('[Replay] Not recording');
      setDebugInfo('Not recording');
      return;
    }

    setIsProcessing(true);
    setDebugInfo('Snapshotting buffer...');

    try {
      // Finalize the in-flight segment so the snapshot includes the freshest
      // video (recording chains straight into a new segment — no coverage gap
      // beyond the recorder's own ~0.1s rotation seam).
      await finalizeActiveSegment();

      const all = segmentsRef.current;
      if (all.length === 0) {
        throw new Error('No data buffered yet');
      }

      // Pick the newest segments covering the requested window. Each segment
      // is a complete standalone webm — the server concats them losslessly
      // and trims to exactly `seconds` during the re-encode.
      let selected;
      if (seconds > 0) {
        selected = [];
        let covered = 0;
        for (let i = all.length - 1; i >= 0 && covered < seconds; i--) {
          selected.unshift(all[i]);
          covered += (all[i].endTs - all[i].startTs) / 1000;
        }
      } else {
        selected = [...all];
      }

      const totalBytes = selected.reduce((s, seg) => s + seg.blob.size, 0);
      console.log('[Replay] Sending', selected.length, 'segments,', totalBytes, 'bytes');
      setDebugInfo('Processing on server...');

      const formData = new FormData();
      selected.forEach((seg, i) => {
        formData.append('segments', seg.blob, `segment_${String(i).padStart(3, '0')}.webm`);
      });
      if (seconds > 0) formData.append('seconds', String(seconds));

      try {
        const response = await fetch('/api/replay/process', {
          method: 'POST',
          headers: authHeaders(),
          body: formData,
        });

        if (response.ok) {
          const meta = await response.json();
          console.log('[Replay] Server replay session:', meta);
          if (replayUrl && replayUrl.startsWith('blob:')) URL.revokeObjectURL(replayUrl);
          // Preview streams straight from the server (range requests) — the
          // processed video is never downloaded wholesale, and trim/upload
          // happen server-side against the id.
          setReplayId(meta.id);
          setReplayUrl(meta.url);
          setIsReplaying(true);
          setDebugInfo(seconds > 0 ? `Last ${seconds}s ready` : 'Ready');
        } else {
          throw new Error('Server processing failed: ' + await response.text());
        }
      } catch (e) {
        console.log('[Replay] Server processing failed, using raw segments:', e);
        // Degraded fallback: back-to-back segment blobs. Not a spec-valid
        // single webm, but Chromium tolerates it well enough to eyeball.
        const url = URL.createObjectURL(
          new Blob(selected.map((s) => s.blob), { type: 'video/webm' }));
        if (replayUrl && replayUrl.startsWith('blob:')) URL.revokeObjectURL(replayUrl);
        setReplayId(null);
        setReplayUrl(url);
        setIsReplaying(true);
        setDebugInfo('Raw video (processing failed)');
      }
    } catch (e) {
      console.error('[Replay] Failed to create replay:', e);
      setDebugInfo('Error: ' + e.message);
    }

    setIsProcessing(false);
  }, [replayUrl, finalizeActiveSegment, authHeaders]);

  const uploadToMatrix = useCallback(async () => {
    if (!replayUrl) return;

    setUploadStatus('uploading');

    try {
      let uploadResponse;
      if (replayId) {
        // Server already holds the processed file — push by id, zero bytes
        // travel through the browser.
        console.log('[Replay] Uploading stored replay', replayId, 'to room', matrixRoomId);
        uploadResponse = await fetch('/api/replay/upload', {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ replay_id: replayId, room_id: matrixRoomId || undefined }),
        });
      } else {
        // Degraded fallback (raw local blob): legacy multipart upload.
        const response = await fetch(replayUrl);
        const blob = await response.blob();
        console.log('[Replay] Uploading', blob.size, 'bytes to room', matrixRoomId);
        const formData = new FormData();
        formData.append('video', blob, `replay-${Date.now()}.mp4`);
        if (matrixRoomId) formData.append('room_id', matrixRoomId);
        uploadResponse = await fetch('/api/replay/upload', {
          method: 'POST',
          headers: authHeaders(),
          body: formData,
        });
      }

      if (uploadResponse.ok) {
        const result = await uploadResponse.json();
        setUploadStatus('success');
        console.log('[Replay] Uploaded to Matrix:', result);
        // Close replay view after successful upload
        setTimeout(() => {
          closeReplay();
        }, 1500);
      } else {
        const error = await uploadResponse.text();
        console.error('[Replay] Upload failed:', error);
        setUploadStatus('error');
        setTimeout(() => setUploadStatus(null), 3000);
      }
    } catch (e) {
      console.error('[Replay] Upload failed:', e);
      setUploadStatus('error');
      setTimeout(() => setUploadStatus(null), 3000);
    }
  }, [replayUrl, replayId, matrixRoomId, authHeaders]);

  const closeReplay = useCallback(() => {
    if (replayUrl && replayUrl.startsWith('blob:')) {
      URL.revokeObjectURL(replayUrl);
    }
    setReplayUrl(null);
    setReplayId(null);
    setIsReplaying(false);
    setShowTrimmer(false);
    setUploadStatus(null);
  }, [replayUrl]);

  const handleTrimComplete = useCallback((result) => {
    console.log('[Replay] Trim/upload complete:', result);
    closeReplay();
  }, [closeReplay]);

  const openTrimmer = useCallback(() => {
    setShowTrimmer(true);
  }, []);

  return (
    <div className="panel instant-replay">
      <h2>Instant Replay</h2>
      <div className="replay-controls">
        <div className="replay-status">
          <div className={`replay-indicator ${isRecording ? 'active' : ''}`} />
          <span className="replay-duration">
            {isRecording
              ? `Buffer: ${bufferDuration.toFixed(0)}s`
              : 'Not recording'}
          </span>
          {debugInfo && <span className="replay-debug"> ({debugInfo})</span>}
        </div>

        {isReplaying ? (
          showTrimmer ? (
            <VideoTrimmer
              videoUrl={replayUrl}
              replayId={replayId}
              onTrimComplete={handleTrimComplete}
              onCancel={() => setShowTrimmer(false)}
              matrixRoomId={matrixRoomId}
              client={client}
            />
          ) : (
            <div className="replay-playback">
              <video
                ref={replayVideoRef}
                className="replay-video"
                src={replayUrl}
                controls
                autoPlay
                playsInline
              />
              <div className="replay-actions">
                <button
                  className="btn btn-secondary"
                  onClick={openTrimmer}
                  title="Trim video before uploading"
                >
                  Trim & Edit
                </button>
                <button
                  className="btn btn-primary"
                  onClick={uploadToMatrix}
                  disabled={uploadStatus === 'uploading'}
                >
                  {uploadStatus === 'uploading' ? 'Uploading...' :
                   uploadStatus === 'success' ? 'Uploaded!' :
                   uploadStatus === 'error' ? 'Failed' :
                   'Upload Full'}
                </button>
                <button className="btn btn-secondary" onClick={closeReplay}>
                  Close
                </button>
              </div>
            </div>
          )
        ) : (
          <div className="replay-buttons">
            {REPLAY_OPTIONS.map(({ label, seconds }) => (
              <button
                key={seconds}
                className={`btn ${seconds === -1 ? 'btn-secondary' : 'btn-primary'}`}
                onClick={() => createReplay(seconds)}
                disabled={
                  isProcessing ||
                  !isRecording ||
                  (seconds > 0 && bufferDuration < seconds)
                }
                title={seconds === -1 ? 'Get full recorded video' : `Get last ${seconds} seconds`}
              >
                {isProcessing ? '...' : label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
